import { Component, OnInit } from '@angular/core';
import { Box, BoxPriceLog } from 'src/app/models/box/box.model';
import { BoxesService } from 'src/app/services/data/boxes.service';

interface AllocationItem {
  label: string;
  value: string;
  percent: number;
}

interface FundInfo {
  key: string;
  nameMatches: string[];
  title: string;
  subtitle: string;
  licenseNo: string;
  fundType: string;
  issuer: string;
  manager: string;
  nominalValue: string;
  minSubscription: string;
  objective: string;
  allocation: AllocationItem[];
  features: string[];
  brochureUrl: string;
}

interface FundNotice {
  nameMatches: string[];
  badge: string;
  title: string;
  body: string;
  highlight: string;
  meta: { label: string; value: string }[];
}

type ChartRange = '1M' | '3M' | '6M' | '1Y' | 'ALL';

interface BoxDisplayItem {
  box: Box;
  fund?: FundInfo;
  notice?: FundNotice;
  logs: BoxPriceLog[];
  chartLabels: string[];
  chartDatasets: any[];
  chartOptions: any;
  chartWindowStart: number;
  chartWindowEnd: number;
  chartLoaded: boolean;
  chartScrollable: boolean;
  selectedRange: ChartRange;
}

const FUNDS: FundInfo[] = [
  {
    key: 'aspire-equity',
    nameMatches: ['وفرة'],
    title: 'صندوق استثمار اسباير كابيتال للأسهم – وفرة بلس',
    subtitle: 'للاستثمار في الأسهم – الإصدار الأول',
    licenseNo: 'ترخيص رقم 1000 لسنة 2025',
    fundType: 'صندوق استثمار أسهم الشركات المدرجة بالبورصة، ذو عائد تراكمي',
    issuer: 'شركة اسباير كابيتال القابضة للاستثمارات المالية',
    manager: 'شركة أيه أي إم للاستثمارات المالية (AIM for Financial Investments)',
    nominalValue: '10 جنيه مصري للوثيقة',
    minSubscription: '100 وثيقة، دون حد أقصى',
    objective: 'يهدف الصندوق إلى الاستثمار في محفظة متنوعة من أسهم الشركات المقيدة ببورصة الأوراق المالية المصرية وأسهم الشركات المصرية المقيدة بالخارج، لتحقيق نمو رأسمالي مع إمكانية إجراء توزيعات نقدية أو في صورة وثائق مجانية، مع مراعاة العلاقة بين العائد والمخاطرة والعمل على إدارة المخاطر من خلال تنويع الاستثمارات وفق الضوابط الاستثمارية المحددة.',
    allocation: [
      { label: 'أسهم', value: '35% حتى 95%', percent: 95 },
      { label: 'أذون وسندات خزانة', value: 'حتى 65%', percent: 65 },
      { label: 'صناديق سيولة نقدية', value: 'حتى 20%', percent: 20 },
      { label: 'ودائع بنكية', value: 'حد أدنى 5%', percent: 5 }
    ],
    features: [
      'إدارة استثمارية محترفة تهتم بتحقيق العائد وإدارة المخاطر',
      'عائد الصندوق والوثيقة معفى من الضرائب',
      'أداة استثمار تناسب المستثمر الراغب في الاستثمار بأسهم الشركات المقيدة لتحقيق عائد على المدى المتوسط والطويل',
      'الاكتتاب والاسترداد يوميًا دون تحمل أي مصروفات أو غرامات، مع إمكانية استرداد أي عدد من الوثائق',
      'عائد يومي تراكمي يضاف على سعر الوثيقة'
    ],
    brochureUrl: 'assets/docs/aspire-equity-fund.pdf'
  },
  {
    key: 'rawaj-cash',
    nameMatches: ['رواج'],
    title: 'صندوق استثمار اسباير كابيتال النقدي – رواج',
    subtitle: 'ذو العائد اليومي',
    licenseNo: 'ترخيص رقم 991 لسنة 2025',
    fundType: 'صندوق استثمار في أدوات السيولة النقدية، ذو عائد تراكمي',
    issuer: 'شركة اسباير كابيتال القابضة للاستثمارات المالية',
    manager: 'شركة أيه أي إم للاستثمارات المالية (AIM for Financial Investments)',
    nominalValue: '100 جنيه مصري للوثيقة',
    minSubscription: '10 وثائق، دون حد أقصى',
    objective: 'يهدف الصندوق إلى تحقيق عائد يومي تراكمي يتناسب مع درجة المخاطر المنخفضة المرتبطة بالأدوات الاستثمارية المستثمر فيها، والتي تتسم بدرجة سيولة عالية، مما يتيح للمستثمر شراء واسترداد الوثائق يوميًا.',
    allocation: [
      { label: 'أذون خزانة مصرية', value: 'حتى 90%', percent: 90 },
      { label: 'ودائع بنكية', value: 'من 50% حتى 100%', percent: 100 },
      { label: 'سندات', value: 'حتى 50%', percent: 50 },
      { label: 'صناديق مماثلة', value: 'حتى 50%', percent: 50 }
    ],
    features: [
      'إدارة استثمارية محترفة تهتم بتحقيق العائد وإدارة المخاطر',
      'عائد الصندوق والوثيقة معفى من الضرائب',
      'أداة استثمار تناسب المستثمر الراغب في إدارة السيولة النقدية الفائضة وتحقيق عائد فوق الودائع البنكية',
      'الاكتتاب والاسترداد يوميًا دون تحمل أي مصروفات أو غرامات، مع إمكانية استرداد أي عدد من الوثائق',
      'عائد يومي تراكمي يضاف على سعر الوثيقة'
    ],
    brochureUrl: 'assets/docs/rawaj-cash-fund.pdf'
  }
];

const FUND_NOTICES: FundNotice[] = [
  {
    nameMatches: ['Zaldi', 'زالدي', 'زالدي ستار'],
    badge: 'إعلان هام',
    title: 'تعديل القيمة الاسمية لوثائق صندوق زالدي ستار',
    body: 'نحيط سيادتكم علمًا بأن الصندوق بصدد تعديل القيمة الاسمية للوثائق المصدرة من صندوق زالدي النقدي ذو العائد اليومي التراكمي والدوري (زالدي ستار) من 100 جنيه مصري إلى 1 جنيه مصري، وفقًا لقرار لجنة الإشراف على الصندوق بتاريخ 24/09/2026 وموافقة الهيئة العامة للرقابة المالية بتاريخ 14/10/2026.',
    highlight: 'سيتم تقسيم الوثيقة على الأرصدة القائمة للعملاء لديكم في تاريخ إقفال يوم 01-11-2026 بالسعر الجديد، على أن يتم استقبال طلبات الشراء والاسترداد يوم الاثنين على السعر الجديد.',
    meta: [
      { label: 'من', value: '100 جم' },
      { label: 'إلى', value: '1 جم' },
      { label: 'تاريخ الإقفال', value: '01-11-2026' }
    ]
  }
];

@Component({
  selector: 'app-boxes',
  templateUrl: './boxes.component.html',
  styleUrls: ['./boxes.component.scss']
})
export class BoxesComponent implements OnInit {

  displayItems: BoxDisplayItem[] = [];
  private readonly visibleDays = 12;
  private panState: { item: BoxDisplayItem; startX: number; startWindow: number } | null = null;

  chartRanges: { value: ChartRange; label: string }[] = [
    { value: '1M', label: 'شهر' },
    { value: '3M', label: '3 أشهر' },
    { value: '6M', label: '6 أشهر' },
    { value: '1Y', label: 'سنة' },
    { value: 'ALL', label: 'الكل' }
  ];

  subscriptionChannels: string[] = [
    'شركة ايون إكس لتداول الأوراق المالية',
    'شركة عربية أون لاين',
    'شركة اسطول لتداول الأوراق المالية'
  ];

  subscriptionSteps: string[] = [
    'فتح حساب وتوقيع عقد تلقي الاكتتابات مع إحدى جهات تلقي الاكتتاب',
    'توقيع طلب الاكتتاب',
    'إيداع مبلغ الاكتتاب',
    'الحصول على إشعار التنفيذ من الجهة متلقية الاكتتاب'
  ];

  constructor(private boxesService: BoxesService) { }

  ngOnInit(): void {
    this.boxesService.getBoxes().subscribe(boxes => {
      this.displayItems = this.groupBoxes(boxes);
      this.displayItems.forEach(item => this.loadHistory(item));
    });
  }

  private groupBoxes(boxes: Box[]): BoxDisplayItem[] {
    const matchFund = (box: Box): FundInfo | undefined =>
      FUNDS.find(f => f.nameMatches.some(alias => box.name?.includes(alias)));

    const matchNotice = (box: Box): FundNotice | undefined =>
      FUND_NOTICES.find(n => n.nameMatches.some(alias => box.name?.includes(alias)));

    const groups = new Map<string, { box: Box; fund?: FundInfo }>();

    for (const box of boxes) {
      const fund = matchFund(box);
      const key = fund ? fund.key : `unmatched:${box.name}`;
      const existing = groups.get(key);
      if (!existing || new Date(box.updatedAt).getTime() > new Date(existing.box.updatedAt).getTime()) {
        groups.set(key, { box, fund });
      }
    }

    return Array.from(groups.values()).map(({ box, fund }) => ({
      box,
      fund,
      notice: matchNotice(box),
      logs: [],
      chartLabels: [],
      chartDatasets: [],
      chartOptions: {},
      chartWindowStart: 0,
      chartWindowEnd: 0,
      chartLoaded: false,
      chartScrollable: false,
      selectedRange: 'ALL' as ChartRange
    }));
  }

  private loadHistory(item: BoxDisplayItem): void {
    this.boxesService.getBoxLogs(item.box.id).subscribe(logs => {
      item.logs = this.deduplicateLogsByDay(logs || []);
      this.applyRange(item, item.selectedRange);
    });
  }

  private deduplicateLogsByDay(logs: BoxPriceLog[]): BoxPriceLog[] {
    const dailyMap = new Map<string, BoxPriceLog>();

    for (const log of logs) {
      const d = new Date(log.changedAt);
      if (isNaN(d.getTime())) {
        continue;
      }
      const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      const existing = dailyMap.get(dayKey);
      if (!existing) {
        dailyMap.set(dayKey, log);
      } else {
        const isLater = (log.id ?? 0) > (existing.id ?? 0) ||
          ((log.id === existing.id || !log.id || !existing.id) &&
            new Date(log.changedAt).getTime() >= new Date(existing.changedAt).getTime());

        if (isLater) {
          dailyMap.set(dayKey, log);
        }
      }
    }

    return Array.from(dailyMap.entries())
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([, log]) => log);
  }

  setRange(item: BoxDisplayItem, range: ChartRange): void {
    item.selectedRange = range;
    this.applyRange(item, range);
  }

  private applyRange(item: BoxDisplayItem, range: ChartRange): void {
    const filtered = this.filterLogsByRange(item.logs, range);
    item.chartLabels = filtered.map(l => new Date(l.changedAt).toLocaleDateString('en-GB'));
    item.chartDatasets = [{
      data: filtered.map(l => l.newPrice),
      label: 'السعر',
      fill: true,
      tension: 0.35,
      borderColor: '#40af95',
      backgroundColor: 'rgba(64, 175, 149, 0.12)',
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: '#40af95',
      pointHitRadius: 15
    }];
    item.chartLoaded = item.logs.length > 1;
    this.initChartWindow(item);
    item.chartOptions = this.buildChartOptions(item);
  }

  onChartWheel(event: WheelEvent, item: BoxDisplayItem): void {
    if (!item.chartScrollable) {
      return;
    }
    event.preventDefault();
    const direction = event.deltaY > 0 ? 1 : -1;
    this.setChartWindow(item, item.chartWindowStart + direction);
  }

  onChartMouseDown(event: MouseEvent, item: BoxDisplayItem): void {
    if (!item.chartScrollable) {
      return;
    }
    this.panState = {
      item,
      startX: event.clientX,
      startWindow: item.chartWindowStart
    };
  }

  onChartMouseMove(event: MouseEvent): void {
    if (!this.panState) {
      return;
    }
    const { item, startX, startWindow } = this.panState;
    const shift = Math.round(-(event.clientX - startX) / 28);
    this.setChartWindow(item, startWindow + shift);
  }

  onChartMouseUp(): void {
    this.panState = null;
  }

  private initChartWindow(item: BoxDisplayItem): void {
    const totalPoints = item.chartLabels.length;
    const visibleCount = Math.min(this.visibleDays, totalPoints);
    item.chartScrollable = totalPoints > visibleCount;
    item.chartWindowEnd = totalPoints - 1;
    item.chartWindowStart = Math.max(0, totalPoints - visibleCount);
  }

  private setChartWindow(item: BoxDisplayItem, newStart: number): void {
    const totalPoints = item.chartLabels.length;
    const visibleCount = Math.min(this.visibleDays, totalPoints);
    const maxStart = Math.max(0, totalPoints - visibleCount);
    item.chartWindowStart = Math.max(0, Math.min(newStart, maxStart));
    item.chartWindowEnd = item.chartWindowStart + visibleCount - 1;
    item.chartOptions = this.buildChartOptions(item);
  }

  private buildChartOptions(item: BoxDisplayItem): any {
    const xScale: any = {
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        minRotation: 45,
        font: { size: 11 }
      }
    };

    if (item.chartScrollable) {
      xScale.min = item.chartWindowStart;
      xScale.max = item.chartWindowEnd;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context: any) => `${context.parsed.y} جنيه`
          }
        }
      },
      scales: {
        x: xScale,
        y: { beginAtZero: false }
      }
    };
  }

  private filterLogsByRange(logs: BoxPriceLog[], range: ChartRange): BoxPriceLog[] {
    if (range === 'ALL') {
      return logs;
    }
    const monthsBack = range === '1M' ? 1 : range === '3M' ? 3 : range === '6M' ? 6 : 12;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - monthsBack);
    return logs.filter(l => new Date(l.changedAt).getTime() >= cutoff.getTime());
  }

}
